import React, { useCallback, useMemo } from 'react';
import { Cart } from '@components/checkout/Cart';
import { NavWrapper } from '@components/layout/Nav';
import type { NavLink } from '@components/layout/Nav/types';
import { GoodfyndSiteSection } from '@features/goodfynd-sites/context/goodfynd-site-context/types';
import {
  isGoodfyndSitesPreview,
  scrollToSection,
} from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import { Header } from '@goodfynd/layout';
import { FlexView, Icon, Menu, type MenuOption, Tag } from '@goodfynd/ui';

import type { GoodfyndSitesHeaderProps } from './types';

import classes from './GoodfyndSitesHeader.module.css';

export function GoodfyndSitesHeader({
  logoImageUrl,
  showUserProfile,
}: GoodfyndSitesHeaderProps) {
  const handleSmoothScroll = useCallback((section: GoodfyndSiteSection) => {
    // Update URL with hash without navigation
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', `#${section}`);
    }

    scrollToSection(section);
  }, []);

  const navigation = useMemo<NavLink[]>(
    () => [
      {
        label: 'Home',
        href: '/',
      },
      {
        label: 'Schedule',
        onClick: () => handleSmoothScroll(GoodfyndSiteSection.Schedule),
      },
      {
        label: 'About Us',
        onClick: () => handleSmoothScroll(GoodfyndSiteSection.AboutUs),
      },
      {
        label: 'Contact Us',
        onClick: () => handleSmoothScroll(GoodfyndSiteSection.ContactUs),
      },
    ],
    [handleSmoothScroll]
  );

  const navigationMobile = useMemo<MenuOption[]>(
    () =>
      navigation.map((item) => ({
        ...item,
        onClick: () => item.onClick?.(),
      })),
    [navigation]
  );

  const isPreview = isGoodfyndSitesPreview();

  return (
    <Header
      containerClassName="page-container padded"
      css={{ zIndex: 500 }}
      theme="white"
      logo={
        <FlexView align="center" gap={8} wrap="nowrap">
          <img
            alt="Goodfynd Sites Logo"
            style={{ maxHeight: 52, maxWidth: 192 }}
            src={
              logoImageUrl ||
              'https://res.cloudinary.com/foodease/image/upload/v1762489077/assets/goodfynd-sites.svg'
            }
          />
          {isPreview && <Tag type="important">Preview</Tag>}
        </FlexView>
      }
      userProfile={
        showUserProfile ? undefined : (
          <FlexView align="center" gap={16}>
            <NavWrapper
              className={classes.nav}
              links={navigation}
              variant="horizontal"
            />

            <Cart />

            <Menu
              anchor={<Icon name="more" />}
              anchorClassName={classes['user-menu-anchor']}
              className={classes['user-menu']}
              options={navigationMobile}
            />
          </FlexView>
        )
      }
    />
  );
}
